import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {buildLadder, LadderEntry} from '../player/manifest-ladder';

/** Codec fourcc -> the name an operator would use. Shared vocabulary with the player dialogs. */
const CODEC_NAMES: Record<string, string> = {
  avc1: 'H.264',
  avc3: 'H.264',
  hvc1: 'HEVC',
  hev1: 'HEVC',
  av01: 'AV1',
  vp09: 'VP9',
  vp9: 'VP9',
};

/**
 * Audio codec string -> name, keyed on the *whole* string, not the fourcc.
 *
 * Video codecs differ by fourcc, audio ones do not: AAC-LC and HE-AACv2 are both `mp4a` and are
 * told apart only by the MPEG-4 audio object type in the suffix -- `.40.2` against `.40.29`. Naming
 * them by fourcc would print "mp4a" for both, which is exactly the distinction the label exists to
 * make: a 128k AAC rendition beside a 64k HE-AACv2 one.
 */
const AUDIO_CODEC_NAMES: Record<string, string> = {
  'mp4a.40.2': 'AAC',
  'mp4a.40.02': 'AAC',
  'mp4a.40.5': 'HE-AAC',
  'mp4a.40.29': 'HE-AACv2',
  'ec-3': 'E-AC-3',
  'ac-3': 'AC-3',
  opus: 'Opus',
  'fLaC': 'FLAC',
};

/**
 * The encoded ladder, in full, beside the player rather than inside it.
 *
 * Outside the player on purpose: the player's own chrome fades out, and this is a verification
 * surface that has to stay readable while watching. It lists every rendition of *both* manifests,
 * because a `vod-default-cmaf-abr-v2` ladder splits codecs across them -- HEVC is HLS-only, AV1 is
 * DASH-only -- so neither manifest alone shows what was produced.
 *
 * Rows come from the manifests, not from the player, and are annotated with playability. The player
 * discards what it cannot play before anything is asked of it, which is how encrypted HEVC came to
 * be invisible in Chrome: the decoder exists, but no key system there will feed it.
 */
@Component({
  selector: 'app-video-ladder',
  templateUrl: './video-ladder.component.html',
  imports: [CommonModule],
})
export class VideoLadderComponent implements OnChanges {
  @Input() m3u8: string;
  @Input() manifest: string;
  /** Whether the content is encrypted, which decides if a key system has to be asked at all. */
  @Input() encrypted = false;
  /** Which manifest the player currently has loaded, so its rows can be marked and selected. */
  @Input() activeStreamType = '';
  /** The rendition the player is on, used only to mark the row. */
  @Input() activeRendition: any = null;

  /** Audio and subtitle renditions the player is offering, for the two dropdowns. */
  @Input() audioRenditions: any[] = [];
  @Input() textRenditions: any[] = [];
  @Input() activeAudioRendition: any = null;
  @Input() activeTextRendition: any = null;

  /** The row an operator clicked. The player resolves it; this component knows no rendition ids. */
  @Output() choose = new EventEmitter<LadderEntry>();
  /**
   * Rendition ids, resolved by the player. Null on `chooseText` means subtitles off.
   *
   * Strings, passed through as the option carries them: `clpp.Rendition.id` is a string, and the
   * player resolves it with `===`. These used to go through `Number()`, so "3" became 3, matched
   * nothing, and a pick in either dropdown was silently dropped.
   */
  @Output() chooseAudio = new EventEmitter<string>();
  @Output() chooseText = new EventEmitter<string | null>();

  entries: LadderEntry[] = [];
  loading = false;
  failed = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Only the manifests decide the content of the table; the active-playback inputs change often
    // and must not refetch. Rebuilding on those would refetch on every rendition switch.
    if (changes['m3u8'] || changes['manifest'] || changes['encrypted']) {
      this._build();
    }
  }

  codecName(codec: string | null): string {
    if (!codec) {
      return '—';
    }
    const key = String(codec).split('.')[0].toLowerCase();
    return CODEC_NAMES[key] ?? key;
  }

  resolution(entry: LadderEntry): string {
    return entry.width && entry.height ? `${entry.width}×${entry.height}` : '—';
  }

  /**
   * Manifest bitrate, in decimal units.
   *
   * An HLS variant's BANDWIDTH includes its audio while a DASH representation's does not, so the
   * two columns are not directly comparable across stream types. That is a property of the
   * manifests, not of the encode, and is left as declared rather than silently adjusted.
   */
  bitrate(entry: LadderEntry): string {
    if (!entry.bandwidth) {
      return '—';
    }
    return entry.bandwidth >= 1_000_000
      ? `${(entry.bandwidth / 1_000_000).toFixed(1)} Mbit/s`
      : `${Math.round(entry.bandwidth / 1000)} kbit/s`;
  }

  /** Is this the row being played right now? Matched on codec and resolution, not on id. */
  isPlaying(entry: LadderEntry): boolean {
    if (entry.noVideo) {
      // Audio only: playing whenever its stream is loaded -- there is no video rendition to match.
      return entry.streamType === this.activeStreamType && !this.activeRendition;
    }
    if (entry.streamType !== this.activeStreamType || !this.activeRendition) {
      return false;
    }
    const same = (a: any, b: any) =>
      String(a ?? '').split('.')[0].toLowerCase() === String(b ?? '').split('.')[0].toLowerCase();
    return same(entry.codec, this.activeRendition.codec)
      && entry.width === this.activeRendition.width
      && entry.height === this.activeRendition.height;
  }

  /** Selectable when it plays at all. A row of the other manifest costs a reload, which is fine. */
  canSelect(entry: LadderEntry): boolean {
    return entry.playable;
  }

  select(entry: LadderEntry): void {
    if (this.canSelect(entry) && !this.isPlaying(entry)) {
      this.choose.emit(entry);
    }
  }

  /**
   * What one audio rendition is, in the terms it differs from the others by.
   *
   * Language comes off the track, bitrate and codec off the rendition. Every part is optional in
   * the player's model and is dropped when absent rather than printed as "undefined" -- the HLS
   * side in particular reports less than the DASH side for the same content.
   */
  audioLabel(rendition: any): string {
    const track = rendition?.track;
    const parts = [
      this.trackName(track),
      track?.channelsCount ? `${track.channelsCount}ch` : null,
      this.audioCodecName(rendition?.codec),
      this.audioBitrate(rendition),
    ].filter(part => !!part);
    return parts.join(' · ');
  }

  /**
   * The track's own label and language, shown as the two separate facts they are.
   *
   * They are not interchangeable, and which of them a manifest carries differs: HLS states a label
   * per rendition (`EXT-X-MEDIA … NAME="English"`), while this DASH manifest states none at all --
   * only `lang="en"` on one AdaptationSet and nothing on the other. Folding them into one field hid
   * that, and the label is what a player's track menu actually shows, so an operator verifying an
   * encode needs to see whether there is one.
   *
   * `no label` is stated outright rather than substituted with the language: a missing menu name is
   * a real finding about the package, not something to paper over.
   */
  trackName(track: any): string {
    const label = track?.label || null;
    const language = track?.language || null;
    if (label && language) {
      return `${label} (${language})`;
    }
    if (label) {
      return label;
    }
    return language ? `no label (${language})` : 'no label, no language';
  }

  /**
   * An audio rendition's bitrate, only where the manifest actually declares one.
   *
   * DASH does: each audio Representation carries its own `bandwidth`, and 131 kbit/s for a 128k rendition
   * is that rendition plus container overhead. HLS does not: `EXT-X-MEDIA` has no bandwidth attribute at
   * all, so whatever the player reports there is not a declared audio bitrate -- on this content it
   * hands back the *video variant's* BANDWIDTH, which read as 2809 kbit/s for a 128k audio rendition.
   *
   * Printing a number that is wrong by a factor of twenty is worse than printing none, so HLS gets
   * none. The rest of the label -- language, channels, codec -- still tells the renditions apart.
   */
  audioBitrate(rendition: any): string | null {
    if (this.activeStreamType !== 'DASH stream' || !rendition?.bandwidth) {
      return null;
    }
    return `${Math.round(rendition.bandwidth / 1000)} kbit/s`;
  }

  /** "mp4a.40.29" -> "HE-AACv2". An unmapped codec keeps its own string, which still says something. */
  audioCodecName(codec: string | null): string | null {
    if (!codec) {
      return null;
    }
    return AUDIO_CODEC_NAMES[String(codec)] ?? String(codec);
  }

  /** Subtitles are named, not measured: label and language, plus a kind where it says something. */
  textLabel(rendition: any): string {
    const track = rendition?.track;
    const parts = [
      this.trackName(track),
      track?.kind && track.kind !== 'subtitle' ? track.kind : null,
    ].filter(part => !!part);
    return parts.join(' · ');
  }

  onAudioChange(value: string): void {
    this.chooseAudio.emit(value);
  }

  onTextChange(value: string): void {
    // The empty option is "off", which is a selection rather than an absence of one.
    this.chooseText.emit(value === '' ? null : value);
  }

  private _build(): void {
    const manifests = [
      {streamType: 'HLS stream', url: this.m3u8, hls: true},
      {streamType: 'DASH stream', url: this.manifest, hls: false},
    ].filter(m => !!m.url);

    if (!manifests.length) {
      this.entries = [];
      return;
    }
    this.loading = true;
    this.failed = false;

    Promise.all(manifests.map(m =>
      fetch(m.url)
        .then(r => r.ok ? r.text() : Promise.reject(r.status))
        // One unreadable manifest must not cost the other one's rows.
        .catch(() => '')
        .then(text => ({streamType: m.streamType, text, hls: m.hls}))))
      .then(fetched => buildLadder(fetched, this.encrypted))
      .then(entries => {
        this.entries = entries;
        this.loading = false;
        this.failed = !entries.length;
      })
      .catch(() => {
        this.entries = [];
        this.loading = false;
        this.failed = true;
      });
  }
}
